import type from './SupplierSuggestion.type';
import { isSkipValue } from '../../../utils/calculationHelpers';

// Efficiently clean up decimal numbers by removing trailing zeros
const safeDecimalCleanup = (value) => {
  if (value == null) return '';
  const str = String(value);
  const decimalIndex = str.indexOf('.');
  if (decimalIndex === -1) return str;

  // Remove trailing zeros
  let end = str.length;
  while (end > decimalIndex && str[end - 1] === '0') {
    end -= 1;
  }
  // Remove decimal point if it's the last character
  if (end > 0 && str[end - 1] === '.') {
    end -= 1;
  }
  return str.slice(0, end) || '0';
};

const initialState = {
  supplierSuggestion: [],
  section: [
    { value: 'bom', label: 'Bill of Materials' },
    { value: 'testing', label: 'Testing' },
    { value: 'measurement', label: 'Measurements' },
    { value: 'care-label', label: 'Care Labels' },
    { value: 'product-information', label: 'Product Information' },
  ],
  componentType: [],
  BOMComponent: [],
  compositionComponent: [],
  units: [],
  supplierData: [],
  BomType: [],
  testingBOMComponent: [],
  colourWays: [],
  TestingLibraries: [],
  TestingLibraryFormData: [],
  MeasurementSuggestion: [],
  CareLabelSuggestion: [],
  CopyMeasurementSuggestion: [],
  productRef: [],
  duplicateCodeModal: { isOpen: false, code: null, status: null },
  brands: [],
  categoryType1: [],
  categoryType2: [],
  placementOptions: [],
  finishedOptions: [],
  bomSubComponentOptions: [],
  suggestedDetials: null,
  updateSuggDetail: null,
};

function convertToThreeDecimalPlaces(data) {
  // Helper function to format numeric values while preserving signed values
  const formatSignedValue = (value) => {
    if (!value || isSkipValue(value)) {
      return value; // Preserve skip values as-is
    }

    const str = value.toString().trim();

    // Check for explicit signs
    const hasExplicitPlus = str.startsWith('+');
    const hasExplicitMinus = str.startsWith('-');

    // Parse the numeric value
    const numericValue = parseFloat(str);

    // If it's not a valid number, return the original value
    if (isNaN(numericValue)) {
      return value;
    }

    // Format to 3 decimal places and remove trailing zeros
    let formatted = numericValue.toFixed(3).replace(/\.?0+$/, '');

    // Restore explicit plus sign if it was originally present
    if (hasExplicitPlus && numericValue >= 0) {
      formatted = `+${formatted}`;
    }

    return formatted;
  };

  return data.map((item) => {
    const updatedSpecPomLibraries =
      item?.specPomLibraries?.map((library) => {
        const updatedGradings =
          library?.gradings?.map((grading) => {
            const gradings = formatSignedValue(grading?.grading);
            const measurment = formatSignedValue(grading?.measurment);

            return {
              ...grading,
              grading: gradings,
              measurment,
            };
          }) || [];
        let tolMinus = parseFloat(library.tolMinus).toFixed(3);
        let tolPlus = parseFloat(library.tolPlus).toFixed(3);

        tolMinus = safeDecimalCleanup(tolMinus);
        tolPlus = safeDecimalCleanup(tolPlus);
        return {
          ...library,
          tolMinus,
          tolPlus,
          gradings: updatedGradings,
        };
      }) || [];

    return {
      ...item,
      specPomLibraries: updatedSpecPomLibraries,
    };
  });
}

const updateMeasurementSugg = (oldArry, newData, types) => {
  const newArry = [...oldArry];
  switch (types) {
    case 'POM':
      newArry[0].pomLibraries = [...newData];
      return newArry;
    case 'SPECPOM':
      newArry[0].specPomLibraries = [...newData];
      return newArry;
    default:
      return oldArry; // Return the original oldArry if types doesn't match POM or SPECPOM
  }
};

const SupplierSuggestionReducer = (state = initialState, action) => {
  switch (action.type) {
    case type.SUPPLIER_LIST:
      return {
        ...state,
        supplierSuggestion: [...state.supplierSuggestion, ...action.payload],
      };
    case type.COMPOSITION_COMPONENT:
      return {
        ...state,
        compositionComponent: [...action.payload],
      };
    case type.UNITS:
      return {
        ...state,
        units: [...action.payload],
      };

    case type.BOM_COMPONENT:
      return {
        ...state,
        BOMComponent: [...action.payload],
      };
    case type.SUPPLIER_DATA:
      return {
        ...state,
        supplierData: [...state.supplierData, ...action.payload],
      };
    case type.BOM_TYPE:
      return {
        ...state,
        BomType: [...action.payload],
      };
    case type.TESTING_BOM_COMPONENT:
      return {
        ...state,
        testingBOMComponent: action.payload,
      };
    case type.COLOUR_WAYS:
      return {
        ...state,
        colourWays: action.payload,
      };
    case type.TESTING_LIBRARIES:
      return {
        ...state,
        TestingLibraries: action.payload,
      };
    case type.TESTING_LIBRARIES_FORM_DATA:
      return {
        ...state,
        TestingLibraryFormData: action.payload,
      };
    case type.SET_MEASUREMENT: {
      const tmp = [...action.payload];
      const convertGradingto3Digit = convertToThreeDecimalPlaces(tmp);
      return {
        ...state,
        CopyMeasurementSuggestion: convertGradingto3Digit,
        MeasurementSuggestion: convertGradingto3Digit,
      };
    }

    case type.SET_CARE_LABEL:
      return {
        ...state,
        CareLabelSuggestion: action.payload,
      };

    case type.PRODUCTREF:
      return {
        ...state,
        productRef: action.payload,
      };

    case type.UPDATE_MEASUREMENT: {
      const measurementSugg = updateMeasurementSugg(state.MeasurementSuggestion, action.payload.data, action.payload.type);
      const convertDecimal = convertToThreeDecimalPlaces(measurementSugg);
      return {
        ...state,
        MeasurementSuggestion: convertDecimal,
      };
    }

    case type.DUPLICTAE_MODAL:
      return {
        ...state,
        duplicateCodeModal: action.payload,
      };
    case type.BRANDS:
      return {
        ...state,
        brands: action.payload,
      };

    case type.CATEGORY1:
      return {
        ...state,
        categoryType1: action.payload,
      };

    case type.CATEGORY2:
      return {
        ...state,
        categoryType2: action.payload,
      };
    case type.SET_SUGGESTION_DETAILS:
      return {
        ...state,
        suggestedDetials: action.payload,
      };
    case type.SET_SUPP_DATA_TO_UPDATE:
      return {
        ...state,
        updateSuggDetail: action.payload,
      };
    case type.PLACEMENT_DATA:
      return {
        ...state,
        placementOptions: action.payload,
      };
    case type.FINISHED_OPTIONS:
      return {
        ...state,
        finishedOptions: action.payload,
      };
    case type.ALL_BOM_SUB_COMPONENT_OPTIONS:
      return {
        ...state,
        bomSubComponentOptions: action.payload,
      };
    case type.CLEAR_BOM_SUPPLIER_SUGGESTION:
      return {
        ...state,
        BOMComponent: [],
      };
    default:
      return state;
  }
};

export default SupplierSuggestionReducer;
